import { type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { isKITProduct } from "~/domains/product/types/ProductData";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { Translation } from "~/app/i18n/Translation";
import ProductImage from "~/domains/product/components/ProductImage";
import { HousingMessages } from "~/domains/housing/components/HousingMessages";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import {
  AliasesParameter,
  GPSDParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
  TariffParameter,
} from "~/domains/product/components/ProductParams";
import Parameter from "~/common/components/Parameter";
import { type AddToCartFormData } from "~/features/addToCart/hooks/useAddToCartNew";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

import classes from "~/features/addToCart/components/v2/AtcProductInfo/AtcProductInfo.module.less";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Box, Stack, styled } from "~/common/mui";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleEndIndicator } from "~/domains/salesCampaign/components/SaleEndIndicator/SaleEndIndicator";

interface Props {
  variant?: "ATW" | "ATC" | "CONTRACT";
  id?: string;
  extraParams?: { label: ReactNode; value: ReactNode }[];
  descriptor: ProductDescriptor;
}

export const PRODUCT_IMAGE_WIDTHS = {
  product: "120px",
  gap: "8px",
  brand: "44px",
  manufacturer: "44px",
};

export const AtcProductInfo = (props: Props) => {
  const { variant = "ATC", extraParams = [], descriptor } = props;

  const { t } = useTranslation();
  const formProps = useFormContext<AddToCartFormData>();
  const isTablet = useIsMinTabletSize();
  const product = descriptor.base;

  const selectedPack = formProps != null ? formProps.watch("selectedPack") : null;
  const packSize = selectedPack?.packSize ?? 1;

  const salesHelper = useSalesHelper(descriptor);
  const hasSales = salesHelper.isProductOnSale();

  const packCode = formProps != null ? formProps.watch("selectedPackCode") : INVALID_PACKAGE_CODE;

  const saleData = salesHelper.forPackage(packCode);
  const saleEndDate = saleData?.endDate;

  return (
    <Stack gap={2} className="scha__atc-product-info" data-prod-article={product.hybrisCode}>
      <div className={"p-relative"}>
        {isTablet && <ProductImage product={product} widths={PRODUCT_IMAGE_WIDTHS} />}
        {hasSales && <SaleTag size={32} className={classes.priceTag} />}
      </div>

      {isKITProduct(product) && (
        <Box sx={{ mt: 1 }}>
          <HousingMessages.NotFinal />
        </Box>
      )}

      <Stack gap={0.5}>
        <ProductDesignation
          product={product}
          clickable
          designation={product.designation}
          size={"title-large"}
          isPreferred={product.preferred}
        />
        <Translation {...product.shortDescription} />
        {saleEndDate && <SaleEndIndicatorStyled endDate={saleEndDate} />}
      </Stack>

      <NewVersionMessage baseProduct={product} />

      <ParametersGrid>
        <OldDesignationParameter label={t("productDetailPage.oldDesignation")} product={product} />
        <SchaefflerIdParameter productDescriptor={descriptor} />
        <AliasesParameter product={product} mode={"13-digits"} />
        {extraParams.map((param, index) => (
          <Parameter key={index} label={param.label} value={param.value} />
        ))}
        {variant === "CONTRACT" ? ( // package
          <Parameter
            label={t("addToCart.contract.packaging")}
            value={t("addToCartPopup.packSize", { amount: packSize, code: packCode })}
          />
        ) : (
          <TariffParameter product={product} />
        )}
        {variant !== "CONTRACT" && <GPSDParameter product={product} />}
      </ParametersGrid>
      {/* {descriptor && <CountryOfOriginDescriptorParam descriptor={descriptor} className={"mt-2"} />} */}
    </Stack>
  );
};

export default AtcProductInfo;

const SaleEndIndicatorStyled = styled(SaleEndIndicator.V2)({
  alignSelf: "flex-start",
});

const ParametersGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: "16px",
  gridTemplateColumns: "1fr",

  [theme.breakpoints.down("tablet")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
}));
